import { createContext, useContext } from 'react'

import { StatusBadgeVariant } from '~/client/src/shared/resources/definitions/types'
import IHierarchyParent from '~/client/src/shared/types/IHierarchyParent'

import { FieldConfig } from '../DynamicForm/types'
import { SelectOption } from '../StruxhubInputs/StruxhubSearchableSelect/StruxhubSearchableSelect'

export interface ResourceReadOnlyField {
  key: string
  label: string
  text: string
  kind: 'text' | 'link' | 'metric' | 'status'
  icon?: React.ReactNode
  statusVariant?: StatusBadgeVariant
}

export interface ResourceEditRecord {
  id: string
  name: string
  code?: string | null
  stxResourceId?: string | null
  isDeleted?: boolean
  templateId?: string | null
  data: Record<string, any>
  // Location root columns edited directly by the resource form (equipment).
  color?: string | null
  iconName?: string | null
  parent?: IHierarchyParent | null
}

interface ResourceEditState {
  recordId: string | null
  record: ResourceEditRecord | null
  isCreationMode: boolean
  /** The current user may only view this existing resource. */
  isLocked: boolean
  isSaving: boolean
  saveError: string | null
  editableFields: FieldConfig[]
  addableFieldOptions: SelectOption[]
  readOnlyFields: ResourceReadOnlyField[]
  loading: boolean
  templateName: string | null
  resourceTypeId: string
  qrCodeUrl: string | null
  formId: string
  isFormSelectorOpen: boolean
}

interface ResourceEditActions {
  close: () => void
  save: (formData: Record<string, unknown>) => Promise<void>
  updateField: (fieldName: string, updates: Partial<FieldConfig>) => void
  revealField: (fieldName: string) => void
  openFormSelector: () => void
  closeFormSelector: () => void
  /** Delete the resource. When set, the header shows a 3-dot overflow menu. */
  onDelete?: () => void
}

export interface ResourceEditContextValue {
  state: ResourceEditState
  actions: ResourceEditActions
}

export const ResourceEditContext =
  createContext<ResourceEditContextValue | null>(null)

export function useResourceEdit(): ResourceEditContextValue {
  const context = useContext(ResourceEditContext)
  if (!context) {
    throw new Error('useResourceEdit must be used within ResourceEdit.Root')
  }
  return context
}

export function useResourceEditState(): ResourceEditState {
  return useResourceEdit().state
}
